import { fetch } from 'cross-fetch'
import type { StatusCodes } from 'http-status-codes'

import { stringify } from '../object/stringify'

export class RequestError extends Error {
  public name = this.constructor.name
}

export class RequestStatusError extends RequestError {
  public name = this.constructor.name
  public statusCode: StatusCodes
  public response: Response

  constructor(
    data: unknown,
    { statusCode, response }: { statusCode: number; response: Response }
  ) {
    super(`Status code (${statusCode}) and body (${stringify(data)})`)

    this.statusCode = statusCode
    this.response = response
  }
}

export class RequestTimeoutError extends RequestError {
  public name = this.constructor.name

  constructor(message: string, public timeoutMs: number) {
    super(message)
  }
}

export type RequestOptions = RequestInit & {
  timeoutMs?: number
}

export const request = async (
  url: string,
  { timeoutMs = 10_000, signal, ...fetchOptions }: RequestOptions
): Promise<Response> => {
  let timeoutReached = false
  const controller = new AbortController()

  const timeoutId = setTimeout(() => {
    timeoutReached = true
    controller.abort()
  }, timeoutMs)

  const handleAbort = () => {
    clearTimeout(timeoutId)
    controller.abort()
  }

  signal?.addEventListener('abort', handleAbort)

  try {
    // This file is the only place which we can use the Fetch API
    // eslint-disable-next-line no-restricted-syntax
    return await fetch(url, {
      signal: controller.signal,
      credentials: 'include',
      ...fetchOptions,
    })
  } catch (error) {
    if (
      timeoutReached &&
      error instanceof Error &&
      error.name === 'AbortError'
    ) {
      throw new RequestTimeoutError(
        `Reached request timeout in ${timeoutMs} ms`,
        timeoutMs
      )
    }

    throw error
  } finally {
    clearTimeout(timeoutId)
    signal?.removeEventListener('abort', handleAbort)
  }
}

export const requestJson = async (
  url: string,
  { headers, ...options }: RequestOptions
): Promise<unknown> => {
  const response = await request(url, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    },
    ...options,
  })

  const data = await response.json()

  if (!response.ok) {
    throw new RequestStatusError(data, {
      statusCode: response.status,
      response,
    })
  }

  return data
}
